/** @jsx isolateComponent */
import React, { ChangeEvent, useState } from 'react';

import { css } from '@emotion/react';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { uniqueId } from '../../../utils/uniqueId';
import { TextInput, TextInputProps } from '../TextInput';

type RenderSubscriptionFormInputOptions = {
  id?: string;
  isDisabled?: boolean;
  isErrored?: boolean;
  isRequired?: boolean;
  isFullWidth?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
};

type RenderSubscriptionFormButtonOptions = {
  size?: 'small' | 'medium';
};

export type Props = {
  /**
   * Optional error message for the input field. If it exists, the field will be
   * considered to have an error.
   */
  inputError?: string;
  /**
   * Is the input full width?
   * @default false
   */
  inputIsFullWidth?: boolean;
  /**
   * `id` for the input field. If this isn't supplied then one will be generated
   * to properly link the `label` to the input.
   */
  inputId?: string;
  /**
   * Indicates whether the field is disabled.
   */
  inputIsDisabled?: boolean;
  /**
   * Indicates whether the field is required.
   */
  inputIsRequired?: boolean;
  /**
   * Text for the `placeholder` of the input field.
   */
  placeholderText: string;
  /**
   * Make input field fluid in the container.
   */
  fluidInput?: boolean;
  /**
   * Rendered button field.
   */
  renderButton: (opts: RenderSubscriptionFormButtonOptions) => React.ReactNode;
  /**
   * Function that returns the rendered input field.
   */
  renderInput?: (opts: RenderSubscriptionFormInputOptions) => React.ReactNode;
  /**
   * Function that gets called on form submit.
   */
  onSubmit?: (subscriptionValue?: string) => void;
};

const breakpoints = {
  tablet: 768,
  desktop: 1230,
};

const Styles = labelStyles('SubscriptionForm', {
  root: css`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 0 auto;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin: 0;
      flex-direction: row;
    }
  `,
  inputField: (fluidInput: boolean) => css`
    display: flex;
    text-align: left;
    ${fluidInput &&
    withModifier(
      'fluid',
      css`
        flex: 1 100%;
        @media screen and (min-width: ${breakpoints.tablet}px) {
          flex: 2;
        }
      `
    )}
  `,
});

/**
 * Wraps an SubscriptionForm and renders a input field and submit button.
 */
export const SubscriptionForm = ({
  inputIsFullWidth = true,
  fluidInput = false,
  ...props
}: // Adding a type reference to the Props type, allowing the style guide software to pick up the full set of props types.
Props) => {
  const [id] = useState<string>(props.inputId || uniqueId('SubscriptionForm-'));
  const [subscribedValue, setSubscribedValue] = useState<string>('');
  const inputSize: TextInputProps['size'] = 'small';
  const inputProps = {
    id,
    isDisabled: props.inputIsDisabled,
    isErrored: !!props.inputError,
    isRequired: props.inputIsRequired,
    isFullWidth: inputIsFullWidth,
    placeholder: props.placeholderText,
    ariaLabel: props.placeholderText,
    size: inputSize,
    onChange: (event: ChangeEvent<HTMLInputElement>) =>
      setSubscribedValue(event.target.value),
  };
  return (
    <form
      onSubmit={() => props.onSubmit?.(subscribedValue)}
      data-testid="subscription-form"
    >
      <div css={Styles.root}>
        <div css={Styles.inputField(fluidInput)}>
          {props.renderInput ? (
            props.renderInput(inputProps)
          ) : (
            <TextInput {...inputProps} />
          )}
        </div>
        {props.renderButton({ size: 'small' })}
      </div>
    </form>
  );
};
